import { useState, useMemo } from "react"
import { useRequest } from "ahooks";
import { Card, Form, Modal, Radio, Button, message, Input } from "antd";
import { EditForm } from "../../../components/editForm";
import { Table, DEFAULT_PAGE_DATA } from "../../../components/table";
import { DEFAULT_QUERY_DATA, generateQueryFormConfig, generateTableColumns } from "./config";
import { AmountServer } from "../../../server";
import dayjs from "dayjs";

const server = AmountServer.InternationalRemittanceList;

export const InternationalRemittanceList = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [queryData, setQueryData] = useState<any>({ ...DEFAULT_QUERY_DATA });
    const [tableData, setTableData] = useState<any[]>([]);
    const [pageData, setPageData] = useState<any>({ ...DEFAULT_PAGE_DATA });
    const [auditModalVisible, setAuditModalVisible] = useState<boolean>(false);
    const [currentRecord, setCurrentRecord] = useState<any>(null);
    const [auditStatus, setAuditStatus] = useState<number | null>(null);
    const [detailModalVisible, setDetailModalVisible] = useState<boolean>(false);
    const [detailRecord, setDetailRecord] = useState<any>(null);
    const [remarkModalVisible, setRemarkModalVisible] = useState<boolean>(false);
    const [remarkRecord, setRemarkRecord] = useState<any>(null);
    const [remarkValue, setRemarkValue] = useState<string>('');

    //获取表格数据
    const { run, refresh } = useRequest(server.list, {
        defaultParams: [{ ...pageData }],
        onBefore: () => setLoading(true),
        onFinally: () => setLoading(false),
        onError: () => {
            setTableData([]);
            setPageData({ ...pageData, Total: 0 });
        },
        onSuccess: (res: any) => {
            const { Total, List, Page, PageSize } = res;
            setTableData(List);
            setPageData({ Page, PageSize, Total });
        }
    });

    // 审核请求
    const { run: runAudit, loading: auditLoading } = useRequest(server.audit, {
        manual: true,
        onSuccess: () => {
            message.success('审核成功');
            setAuditModalVisible(false);
            setAuditStatus(null);
            refresh();
        },
        onError: (error) => {
            message.error('审核失败: ' + error.message);
        }
    });

    // 更新备注请求
    const { run: runUpdateRemark, loading: remarkLoading } = useRequest(
        (params: any) => server.updateRemark(params),
        {
            manual: true,
            onSuccess: () => {
                message.success('备注更新成功');
                setRemarkModalVisible(false);
                setRemarkValue('');
                setRemarkRecord(null);
                refresh();
            },
            onError: (error) => {
                message.error('备注更新失败: ' + error.message);
            }
        }
    );

    const getQueryFormConfig = useMemo(() => {
        return generateQueryFormConfig();
    }, []);

    const getTableColumns = useMemo(() => {
        const baseColumns = generateTableColumns();
        // 添加操作列
        return [
            ...baseColumns,
            {
                title: "操作",
                key: "action",
                fixed: "right",
                render: (_: any, record: any) => {
                    // 只在待处理（Status = 10）时显示审核按钮
                    if (record.Status === 10) {
                        return (
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <Button
                                    type="primary"
                                    size="small"
                                    onClick={() => {
                                        setCurrentRecord(record);
                                        setAuditModalVisible(true);
                                    }}
                                >
                                    审核
                                </Button>
                                <Button
                                    size="small"
                                    onClick={() => {
                                        setDetailRecord(record);
                                        setDetailModalVisible(true);
                                    }}
                                >
                                    更多
                                </Button>
                                <Button
                                    size="small"
                                    onClick={() => {
                                        setRemarkRecord(record);
                                        setRemarkValue(record.UpstreamReason || '');
                                        setRemarkModalVisible(true);
                                    }}
                                >
                                    备注
                                </Button>
                            </div>
                        );
                    }
                    return (
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <Button
                                size="small"
                                onClick={() => {
                                    setDetailRecord(record);
                                    setDetailModalVisible(true);
                                }}
                            >
                                更多
                            </Button>
                            <Button
                                size="small"
                                onClick={() => {
                                    setRemarkRecord(record);
                                    setRemarkValue(record.UpstreamReason || '');
                                    setRemarkModalVisible(true);
                                }}
                            >
                                备注
                            </Button>
                        </div>
                    );
                },
            }
        ];
    }, []);

    const formatQueryDate = () => {
        let { DateStart, DateEnd, ...otherData } = queryData;
        const format = 'YYYYMMDD'
        DateStart = DateStart ? dayjs(DateStart).startOf('day').format(format) : null;
        DateEnd = DateEnd ? dayjs(DateEnd).endOf('day').format(format) : null;
        return { ...otherData, DateStart, DateEnd };
    };

    const handleAudit = () => {
        if (auditStatus === null) {
            message.warning('请选择审核结果');
            return;
        }

        runAudit({
            Id: currentRecord.Id,
            Status: auditStatus
        });
    };

    return (
        <div>
            <Card>
                <div className="flex space-between align-center ">
                    <EditForm
                        componentId="international-remittance-query-form"
                        loading={loading}
                        formAttrsProps={{
                            layout: 'inline',
                        }}
                        formData={queryData}
                        onDataChange={setQueryData}
                        formConfig={getQueryFormConfig}
                        onQuery={() => {
                            run({ ...DEFAULT_PAGE_DATA, ...formatQueryDate() });
                        }}
                        onReset={() => {
                            setQueryData({ ...DEFAULT_QUERY_DATA });
                            run({ ...DEFAULT_QUERY_DATA, ...DEFAULT_PAGE_DATA });
                        }}
                    />
                </div>
            </Card>
            <Card className="mt20">
                <Table
                    loading={loading}
                    dataSource={tableData}
                    columns={getTableColumns}
                    rowKey="Id"
                    scroll={{
                        x: 'max-content'
                    }}
                    page={pageData}
                    onPageChange={(Page: number, PageSize: number) => {
                        const currentPage = { ...pageData, Page, PageSize };
                        run({ ...currentPage, ...formatQueryDate() });
                    }}
                />
            </Card >

            {/* 审核弹窗 */}
            <Modal
                title="审核"
                open={auditModalVisible}
                onCancel={() => {
                    setAuditModalVisible(false);
                    setAuditStatus(null);
                }}
                footer={[
                    <Button
                        key="cancel"
                        onClick={() => {
                            setAuditModalVisible(false);
                            setAuditStatus(null);
                        }}
                    >
                        取消
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={auditLoading}
                        onClick={handleAudit}
                    >
                        确定
                    </Button>
                ]}
            >
                <Radio.Group
                    onChange={(e) => setAuditStatus(e.target.value)}
                    value={auditStatus}
                >
                    <Radio value={40}>通过</Radio>
                    <Radio value={20}>拒绝</Radio>
                </Radio.Group>
            </Modal>

            {/* 详情弹窗 */}
            <Modal
                title="订单详情"
                open={detailModalVisible}
                onCancel={() => {
                    setDetailModalVisible(false);
                    setDetailRecord(null);
                }}
                footer={[
                    <Button
                        key="close"
                        type="primary"
                        onClick={() => {
                            setDetailModalVisible(false);
                            setDetailRecord(null);
                        }}
                    >
                        关闭
                    </Button>
                ]}
            >
                <div style={{ marginBottom: '16px' }}>
                    {/* 基本信息 */}
                    <div style={{ marginBottom: '12px', fontWeight: 'bold', marginTop: '16px', paddingBottom: '8px', }}>订单信息</div>
                    <div style={{ marginBottom: '8px' }}>
                        <strong>订单号:</strong> {detailRecord?.OrderNo || '-'}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                        <strong>交易时间:</strong> {detailRecord?.CreatedAtString || '-'}
                    </div>

                    {/* 费用信息 */}
                    <div style={{ marginBottom: '12px', fontWeight: 'bold', marginTop: '16px', paddingBottom: '8px', }}>费用信息</div>
                    <div style={{ marginBottom: '8px' }}>
                        <strong>手续费:</strong> {detailRecord?.WalletAmountServiceFee || '-'} USD
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                        <strong>汇兑费:</strong> {detailRecord?.ExchangeFeeRate ? `(${(detailRecord.ExchangeFeeRate * 100).toFixed(2)}%)` : ''} {detailRecord?.WalletAmountExchangeFee || '-'} USD
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                        <strong>汇率:</strong> 1 USD ≈ {detailRecord?.ApplyExchangeRate || '-'} {detailRecord?.BankAccountCurrencyCode || ''}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                        <strong>收款金额:</strong> {detailRecord?.TransferAmount || '-'} {detailRecord?.BankAccountCurrencyCode || ''}
                    </div>

                    {/* 汇款方式 */}
                    <div style={{ marginBottom: '12px', fontWeight: 'bold', marginTop: '16px', paddingBottom: '8px', }}>汇款方式</div>
                    <div style={{ marginBottom: '8px' }}>
                        <strong>汇款通道:</strong> {detailRecord?.ChannelName || '-'}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                        <strong>到账时间:</strong> {detailRecord?.ChannelCreditedTimeName || '-'}
                    </div>

                    {/* 收款信息 */}
                    <div style={{ marginBottom: '12px', fontWeight: 'bold', marginTop: '16px', paddingBottom: '8px', }}>收款信息</div>
                    <div style={{ marginBottom: '8px' }}>
                        <strong>收款人:</strong> {detailRecord?.TransferBeneficiary.BankAccountHolder || '-'}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                        <strong>收款国家:</strong> {detailRecord?.Country.NameCn || '-'}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                        <strong>收款银行:</strong> {detailRecord?.BankName || '-'}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                        <strong>银行账号:</strong> {detailRecord?.BankNo || '-'}
                    </div>
                    <div style={{ marginBottom: '8px' }}>
                        <strong>开户行:</strong> {detailRecord?.BankBranchName || '-'}
                    </div>
                    <div>
                        <strong>汇款目的:</strong> {detailRecord?.Purpose || '-'}
                    </div>
                </div>
            </Modal>

            {/* 备注弹窗 */}
            <Modal
                title="修改备注"
                open={remarkModalVisible}
                onCancel={() => {
                    setRemarkModalVisible(false);
                    setRemarkValue('');
                    setRemarkRecord(null);
                }}
                footer={[<Button
                    key="close"
                    onClick={() => {
                        setRemarkModalVisible(false);
                        setRemarkValue('');
                        setRemarkRecord(null);
                    }}
                >
                    取消
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={remarkLoading}
                    onClick={() => {
                        if (!remarkRecord) return;
                        runUpdateRemark({
                            Id: remarkRecord.Id,
                            UpstreamReason: remarkValue
                        });
                    }}
                >
                    确定
                </Button>]}
            >
                <Form layout="vertical">
                    <Form.Item label="备注">
                        <Input.TextArea
                            rows={4}
                            value={remarkValue}
                            onChange={(e) => setRemarkValue(e.target.value)}
                            placeholder="请输入备注内容"
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
